import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AssumptionsProvider } from "./context/AssumptionsContext";
import { BrandingProvider } from "./context/BrandingContext";
import { AuthProvider } from "./context/AuthContext";
import { ProfileProvider } from "./context/ProfileContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { Header } from "./components/Header";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";
import { Home } from "./pages/Home";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { PoliciesPage } from "./pages/PoliciesPage";
import { AnalyzePolicyPage } from "./pages/AnalyzePolicyPage";
import { NetWorthPage } from "./pages/NetWorthPage";
import { BudgetPage } from "./pages/BudgetPage";
import { PortfolioTimelinePage } from "./pages/PortfolioTimelinePage";
import { ChatPage } from "./pages/ChatPage";
import { CalculatorsPage } from "./pages/CalculatorsPage";
import { BottomTabBar } from "./components/BottomTabBar";
import { PremiumCalendarPage } from "./pages/PremiumCalendarPage";
import { ProfilePage } from "./pages/ProfilePage";
import { MyScenariosPage } from "./pages/MyScenariosPage";
import { CalculatorPage } from "./calculators/CalculatorPage";
import { DebtFreedomPage } from "./tools/DebtFreedomPage";
import { CoverageGapPage } from "./pages/CoverageGapPage";
import { ReportsPage } from "./pages/ReportsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminLoginPage } from "./pages/admin/AdminLoginPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminMembersUnifiedPage } from "./pages/admin/AdminMembersUnifiedPage";
import { AdminMemberDetailPage } from "./pages/admin/AdminMemberDetailPage";
import { AdminAdminsPage } from "./pages/admin/AdminAdminsPage";
import { AdminAuditLogPage } from "./pages/admin/AdminAuditLogPage";
import { AdminBrandingPage } from "./pages/admin/AdminBrandingPage";
import { AdminReminderTemplatesPage } from "./pages/admin/AdminReminderTemplatesPage";
import { AdminBudgetCategoriesPage } from "./pages/admin/AdminBudgetCategoriesPage";
import { AdminInsurerKpisPage } from "./pages/admin/AdminInsurerKpisPage";
import { AdminRequestsPage } from "./pages/admin/AdminRequestsPage";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage";
import { AdminTaxonomyPage } from "./pages/admin/AdminTaxonomyPage";
import { AdminPagesPage } from "./pages/admin/AdminPagesPage";
import { PublicPage } from "./pages/PublicPage";
import { Footer } from "./components/Footer";

/** Renders the member <Header /> only on the authenticated member routes so the admin portal
 * and the public landing page each get their own navigation. */
function ConditionalMemberHeader() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  if (pathname === "/" || pathname === "/login") return null;
  return <Header />;
}

/** Same predicate as the Header, but for the mobile bottom-tab-bar. Rendered inside
 * ProtectedRoute could not access location cleanly without duplication, so we mount at the
 * top level and let this component decide. */
function ConditionalBottomTabBar() {
  const { pathname } = useLocation();
  if (!pathname.startsWith("/app")) return null;
  return <BottomTabBar />;
}

function App() {
  return (
    <ThemeProvider>
      <AssumptionsProvider>
        <BrandingProvider>
          <BrowserRouter>
          <AuthProvider>
            <ProfileProvider>
              <AdminAuthProvider>
                <div className="min-h-screen bg-bg text-text pb-16 sm:pb-0">
                  <ConditionalMemberHeader />
                  <Routes>
                    {/* Public */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/page/:slug" element={<PublicPage />} />

                    {/* Admin */}
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route element={<AdminProtectedRoute />}>
                      <Route path="/admin" element={<AdminDashboardPage />} />
                      <Route path="/admin/enrolments" element={<Navigate to="/admin/members" replace />} />
                      <Route path="/admin/members" element={<AdminMembersUnifiedPage />} />
                      <Route path="/admin/members/:id" element={<AdminMemberDetailPage />} />
                      <Route path="/admin/admins" element={<AdminAdminsPage />} />
                      <Route path="/admin/requests" element={<AdminRequestsPage />} />
                      <Route path="/admin/settings" element={<AdminSettingsPage />} />
                      <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
                      <Route path="/admin/branding" element={<AdminBrandingPage />} />
                      <Route path="/admin/reminder-templates" element={<AdminReminderTemplatesPage />} />
                      <Route path="/admin/insurer-kpis" element={<AdminInsurerKpisPage />} />
                      <Route path="/admin/budget-categories" element={<AdminBudgetCategoriesPage />} />
                      <Route path="/admin/taxonomy" element={<AdminTaxonomyPage />} />
                      <Route path="/admin/pages" element={<AdminPagesPage />} />
                    </Route>

                    {/* Member */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/app" element={<Home />} />
                      {/* Legacy path aliases — keep working links from marketing / older
                         emails / Chatbase deep-links even after we renamed pages. */}
                      <Route path="/app/analyze-policy" element={<Navigate to="/app/policies/analyze" replace />} />
                      <Route path="/app/analyze-policy/:id" element={<Navigate to="/app/policies/analyze" replace />} />
                      <Route path="/app/scenarios" element={<Navigate to="/my-scenarios" replace />} />
                      <Route path="/app/policies" element={<PoliciesPage />} />
                      <Route path="/app/policies/analyze" element={<AnalyzePolicyPage />} />
                      <Route path="/app/policies/calendar" element={<PremiumCalendarPage />} />
                      <Route path="/app/policies/coverage-gap" element={<CoverageGapPage />} />
                      <Route path="/app/net-worth" element={<NetWorthPage />} />
                      <Route path="/app/budget" element={<BudgetPage />} />
                      <Route path="/app/portfolio/timeline" element={<PortfolioTimelinePage />} />
                      <Route path="/app/chat" element={<ChatPage />} />
                      <Route path="/app/calculators" element={<CalculatorsPage />} />
                      <Route path="/calculators/:key" element={<CalculatorPage />} />
                      <Route path="/debt-freedom" element={<DebtFreedomPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/reports/:reportKey" element={<ReportsPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/my-scenarios" element={<MyScenariosPage />} />
                    </Route>

                    {/* Global 404 — must be the last route so it only matches when nothing
                        else did. React Router v6 renders NOTHING by default when no route
                        matches, so without this every mistyped URL used to render a blank
                        body beneath the header. */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  <ConditionalBottomTabBar />
                  <Footer />
                </div>
                <Toaster theme="dark" position="bottom-right" />
              </AdminAuthProvider>
            </ProfileProvider>
          </AuthProvider>
        </BrowserRouter>
        </BrandingProvider>
      </AssumptionsProvider>
    </ThemeProvider>
  );
}

export default App;
